import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PayoutsTable = ({ title, status, fetchData, onRequestWithdraw }) => {
    const [data, setData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const loadData = async (page = currentPage, size = perPage) => {
        setLoading(true);
        try {
            const res = await fetchData(status, page, size);
            setData(res?.data || []);
            setTotalRows(res.pagination?.totalItems || 0);
            setCurrentPage(res.pagination?.page || 1);
        } catch (err) {
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [status, currentPage, perPage]);

    const handleDownload = () => {
        const wsData = data.map(p => {
            const base = {
                Reference: p.referenceCode || '-',
                Amount: p.amount,
                Status: p.status,
                RequestedAt: new Date(p.createdAt).toLocaleString(),
                UpdatedAt: new Date(p.updatedAt).toLocaleString(),
            };

            if (status === 'completed') {
                base.ProcessedBy = p.processedBy?.name || '-';
                base.Email = p.processedBy?.email || '-';
                base.ProcessedAt = new Date(p.processedAt).toLocaleString() || '-';
            }

            if (p.status === 'cancelled') {
                base.CancelledReason = p.cancelledReason || '-';
            }

            return base;
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, 'Payouts');
        XLSX.writeFile(wb, `${title.replace(/\s/g, '-')}-${Date.now()}.xlsx`);
    };

    const totalPages = Math.ceil(totalRows / perPage);

    return (
        <div className="container mt-4 animate__animated animate__fadeIn">
            <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
                <h4 className="fw-bold mb-0">{title}</h4>
                <div className="d-flex flex-wrap gap-2">
                    <button
                        className="btn btn-dark"
                        onClick={handleDownload}
                    >
                        Download Excel
                    </button>
                    <button
                        className="btn btn-outline-dark"
                        onClick={async () => {
                            await onRequestWithdraw?.();
                            loadData(1); // refresh from page 1
                        }}
                    >
                        Request Withdraw
                    </button>

                </div>
            </div>

            <div className="table-responsive">
            <table className="table table-bordered table-striped table-hover text-center" style={{ color: '#000' }}>
                <thead className="table-dark">
                    <tr>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Requested At</th>
                        <th>Processed At</th>
                        <th>Cancelled Reason</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan="5">
                                <div className="spinner-border text-secondary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </td>
                        </tr>
                    ) : data.length === 0 ? (
                        <tr><td colSpan="5">No data available</td></tr>
                    ) : (
                        data.map((row, idx) => (
                            <tr key={idx}>
                                <td>KWD {row.amount?.toFixed(2)}</td>
                                <td>
                                    <span
                                        style={{
                                            textTransform: "capitalize"
                                        }}
                                        className={`badge px-3 py-2 ${row.status === 'paid'
                                            ? 'bg-success'
                                            : row.status === 'pending'
                                                ? 'bg-warning text-dark'
                                                : 'bg-danger'}`}>
                                        {row.status}
                                    </span>
                                </td>
                                <td>{new Date(row.createdAt).toLocaleString()}</td>
                                <td>{row.processedAt ? new Date(row.processedAt).toLocaleString() : "—"} </td>
                                <td>{row.cancelledReason || '-'}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            </div>

            <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mt-3">
                <div>
                    Page {currentPage} of {totalPages} — Showing {(currentPage - 1) * perPage + 1}-
                    {Math.min(currentPage * perPage, totalRows)} of {totalRows}
                </div>

                <div className="btn-group">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                        title="Previous"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        title="Next"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PayoutsTable;
